import { ReversibleOperation } from './RoiMemento';

export class UndoRedoManager {
    private states: ReversibleOperation[] = [];
    private currentIndex = -1;
    private listeners = new Set<() => void>();

    private notifyListeners(): void {
        this.listeners.forEach((listener) => {
            listener();
        });
    }

    constructor(private readonly maxStackSize = 10) {}

    /**
     * Subscribe to state changes
     * @param listener - Function called whenever undo/redo state changes
     * @returns Subscription object with unsubscribe method
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.listeners.add(listener);
        listener();
        return {
            unsubscribe: () => {
                this.listeners.delete(listener);
            },
        };
    }

    /**
     * Add an operation to the history
     */
    addOperation(operation: ReversibleOperation): void {
        if (this.currentIndex < this.states.length - 1) {
            this.states = this.states.slice(0, this.currentIndex + 1);
        }
        this.states.push(operation);
        if (this.states.length > this.maxStackSize) {
            this.states.shift();
        }

        this.currentIndex = this.states.length - 1;
        this.notifyListeners();
    }

    /**
     * Undo - move pointer backwards and return the operation to restore to
     */
    undo(): ReversibleOperation | undefined {
        if (this.states.length === 0 || this.currentIndex <= 0) {
            return undefined;
        }
        this.currentIndex--;

        // Return the operation we're moving TO (previous state)
        const result =
            this.currentIndex >= 0 ? this.states[this.currentIndex] : undefined;
        this.notifyListeners();
        return result;
    }

    /**
     * Redo - move pointer forwards and return the operation to restore to
     */
    redo(): ReversibleOperation | undefined {
        if (this.currentIndex >= this.states.length - 1) {
            return undefined;
        }
        this.currentIndex++;

        // Return the operation we're moving TO (next state)
        const result = this.states[this.currentIndex];
        this.notifyListeners();
        return result;
    }

    /**
     * Jump to specific state by index
     */
    jumpToState(index: number): ReversibleOperation | undefined {
        if (index < -1 || index >= this.states.length) {
            return undefined;
        }
        this.currentIndex = index;
        const result = index >= 0 ? this.states[index] : undefined;
        this.notifyListeners();
        return result;
    }

    /**
     * Get all operations with current state indicator for activity log
     */
    getHistory(): Array<{
        operation: ReversibleOperation;
        isCurrent: boolean;
    }> {
        return this.states.map((op, index) => ({
            operation: op,
            isCurrent: index === this.currentIndex,
        }));
    }

    canUndo(): boolean {
        return this.currentIndex > 0;
    }

    canRedo(): boolean {
        return this.currentIndex < this.states.length - 1;
    }

    clear(): void {
        this.states = [];
        this.currentIndex = -1;
        this.notifyListeners();
    }

    get size(): number {
        return this.states.length;
    }
}
